<template>
  <div>
    <PatternHero
      size="medium"
      color-class="text-primary-400"
      :icon-for-type="iconForType"
    >
      <template v-if="$slots.typeIcon" #typeIcon>
        <slot name="typeIcon"></slot>
      </template>
      <div class="flex flex-col items-start justify-center p-6 md:pl-0">
        <span v-if="updatedAt" class="text-gray-400 ml-auto">
          {{
            $t('labels.updated_at', {
              date: updatedAt,
            })
          }}
        </span>
        <h2
          class="text-mobile-2xl font-semibold first-letter:uppercase md:text-2xl"
          v-html="title"
        />
        <h3
          v-if="$slots.heroSubtitle"
          class="text-mobile-xl md:mt-2 md:text-lg"
        >
          <slot name="heroSubtitle"></slot>
        </h3>
        <p
          v-if="$slots.heroDescription"
          class="text-mobile-lg md:mt-2 md:text-lg"
        >
          <slot name="heroDescription"></slot>
        </p>
        <p
          v-if="$slots.heroDetails"
          class="text-mobile-lg text-shade-500 md:mt-2 md:text-lg"
        >
          <slot name="heroDetails"></slot>
        </p>
        <div v-if="$slots.heroMisc" class="md:mt-2">
          <slot name="heroMisc"></slot>
        </div>
      </div>
    </PatternHero>
    <div class="mx-auto max-w-7xl px-6 pt-6 md:pt-10 xl:px-0">
      <!-- Mobile SidebarMenu -->
      <div v-if="!route.query?.print" class="flex flex-col gap-6 md:hidden">
        <DataCollapse>
          <template #title>
            <span v-if="outline.title">{{ outline.title }}</span>
            <span v-else> {{ $t('labels.menu') }}</span></template
          >
          <NavigationSidebarMenu :menu="outline" class="mt-6" />
        </DataCollapse>
        <DataCollapse
          v-if="actions.length > 0"
          :class="actionsBefore ? ' order-first' : ''"
        >
          <template #title>{{ $t('labels.action', actions.length) }}</template>
          <!-- Actions -->
          <BasicButton
            v-for="action in actions"
            :key="action.label"
            plain
            class="mt-4 w-full"
            :loading="action.label === 'Print version' && printLoading"
            @click="action.action"
          >
            <span class="first-letter:uppercase">{{ action.label }}</span>
          </BasicButton>
        </DataCollapse>
        <slot name="sidebarButtons"></slot>
      </div>
      <div class="flex gap-6 lg:gap-10 xl:gap-16">
        <!-- Desktop SidebarMenu -->
        <div
          v-if="!route.query?.print"
          class="hidden basis-64 print:hidden md:block lg:basis-80"
        >
          <div :class="stickySidebar ? 'sticky top-10' : ''">
            <div
              :class="
                stickySidebar
                  ? 'overflow-y-scroll px-2.5 max-h-[calc(100vh)]'
                  : ''
              "
            >
              <SearchView :in-home="false" class="flex-wrap" />
              <div class="flex flex-col">
                <div v-if="!isWork" class="mr-1 mt-10">
                  <h3
                    v-if="outline.title"
                    class="mb-2 text-xl font-semibold first-letter:uppercase"
                  >
                    {{ outline.title }}
                  </h3>
                  <NavigationSidebarMenu :menu="outline" />
                </div>

                <div v-if="$slots.sidebarMisc" class="mt-10">
                  <slot name="sidebarMisc"></slot>
                </div>
                <div v-if="$slots.sidebarButtons" class="mt-10">
                  <slot name="sidebarButtons"></slot>
                </div>

                <!-- Actions -->
                <div
                  v-if="actions.length > 0"
                  :class="actionsBefore ? ' order-first' : ''"
                >
                  <h3
                    class="mt-2 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg"
                  >
                    {{ $t('labels.action', actions.length) }}
                  </h3>
                  <div v-for="action in actions" :key="action.label">
                    <span class="mt-2" v-if="action.label === 'Mail version'">
                      <hr class="my-2 bg-gray-200 border-0 h-0.5" />
                      Expiration date for the links in the email :
                      <input
                        type="date"
                        :value="expiryDate"
                        @change="emit('changeDate', $event.target.value)"
                      />
                    </span>
                    <BasicButton
                      plain
                      class="mt-4 w-full"
                      :loading="
                        action.label === 'Print version' && printLoading
                      "
                      @click="action.action"
                    >
                      <span class="first-letter:uppercase">{{
                        $t(action.label)
                      }}</span>
                    </BasicButton>
                  </div>
                </div>
              </div>
              <p v-if="$slots.sidebarDetails" class="mt-10 text-shade-500">
                <slot name="sidebarDetails"></slot>
              </p>
              <!-- Desktop Sidebar details -->
              <p v-if="!route.query?.print" class="mt-4 text-shade-500 md:mt-6">
                {{ $t('detail.notice_error_question') }}
              </p>
              <BasicButton
                v-if="!route.query?.print"
                class="mt-4"
                plain
                @click="goToContact"
              >
                <template #iconLeft>
                  <IconMail class="h-4 w-4" />
                </template>
                <span class="first-letter:uppercase">
                  {{ $t('labels.contact_us') }}
                </span>
              </BasicButton>
            </div>
          </div>
        </div>
        <div class="flex-1">
          <slot></slot>
          <div
            v-for="(copyright, i) in copyrights"
            :key="`copyright-${i}`"
            class="mt-6 md:mt-10"
          >
            © {{ copyright.name }}
          </div>
          <slot name="contentMisc"></slot>
          <!-- Mobile Sidebar details -->
          <div v-if="!route.query?.print" class="md:hidden">
            <hr v-if="$slots.sidebarMisc" class="mt-6 border-shade-200" />
            <div v-if="$slots.sidebarMisc" class="mt-6">
              <slot name="sidebarMisc"></slot>
            </div>
            <hr class="mt-6 border-shade-200" />
            <p v-if="$slots.sidebarDetails" class="mt-6 text-shade-500">
              <slot name="sidebarDetails"></slot>
            </p>
            <p class="mt-4 text-shade-500">
              {{ $t('detail.notice_error_question') }}
            </p>
            <BasicButton class="mt-4" plain>
              <template #iconLeft>
                <IconMail class="h-4 w-4" />
              </template>
              <span class="first-letter:uppercase">
                {{ $t('labels.contact_us') }}
              </span>
            </BasicButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import IconMail from '~/assets/icon/general/Mail.svg'
const route = useRoute()
const isWork = ref(false)

const emit = defineEmits(['changeDate'])

defineProps({
  title: {
    type: String,
    required: true,
  },
  updatedAt: {
    type: String,
    default: null,
  },
  creationDate: {
    type: Date,
  },
  outline: {
    type: Object as PropType<SidebarMenu>,
    required: true,
  },
  iconForType: {
    type: String as PropType<ResourceType>,
    default: null,
  },
  copyrights: {
    type: Array as PropType<Copyright[]>,
    default: () => [],
  },
  stickySidebar: {
    type: Boolean,
    default: false,
  },
  actions: {
    type: Array as PropType<Actions>,
    default: () => [],
  },
  /**
   * True if the Actions are placed before the menu
   */
  actionsBefore: {
    type: Boolean,
    default: false,
  },
  printLoading: {
    type: Boolean,
    default: false,
  },
  expiryDate: {
    type: Date,
  },
})
const goToContact = () => {
  navigateTo('/contact')
}
</script>
